import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { IndianRupee, TrendingUp, Calculator as CalcIcon } from 'lucide-react';

const InteractiveCrorepatiPage = () => {
  const [inputs, setInputs] = useState({
    targetAmount: 10000000,
    years: 10,
    annualReturn: 12
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calculators/goal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_amount: parseFloat(inputs.targetAmount),
          years: parseInt(inputs.years),
          annual_return_rate: parseFloat(inputs.annualReturn)
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setResult(data.data);
      }
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
              <IndianRupee className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Crorepati Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Calculate how much you need to invest monthly to become a Crorepati!
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <CalcIcon className="w-6 h-6" />
                Enter Your Goals
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-white mb-2 text-sm font-medium">
                    Target Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={inputs.targetAmount}
                    onChange={(e) => setInputs({...inputs, targetAmount: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                    placeholder="10000000"
                  />
                  <p className="text-white/50 text-xs mt-1">Default: ₹1 Crore</p>
                </div>

                <div>
                  <label className="block text-white mb-2 text-sm font-medium">
                    Time Period (Years)
                  </label>
                  <input
                    type="number"
                    value={inputs.years}
                    onChange={(e) => setInputs({...inputs, years: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                    placeholder="10"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2 text-sm font-medium">
                    Expected Annual Return (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={inputs.annualReturn}
                    onChange={(e) => setInputs({...inputs, annualReturn: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                    placeholder="12"
                  />
                  <p className="text-white/50 text-xs mt-1">Typical equity returns: 10-15%</p>
                </div>

                <button
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full py-3 bg-white text-black font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? 'Calculating...' : 'Calculate SIP Required'}
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Your Investment Plan
              </h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                    <p className="text-white/70 text-sm mb-2">Monthly SIP Required</p>
                    <p className="text-3xl font-bold text-white">
                      {formatCurrency(result.monthly_sip_required)}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-white/70 text-sm mb-1">Total Investment</p>
                      <p className="text-xl font-semibold text-white">
                        {formatCurrency(result.total_investment)}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-white/70 text-sm mb-1">Wealth Gained</p>
                      <p className="text-xl font-semibold text-green-400">
                        {formatCurrency(result.wealth_gained)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-white/70 text-sm mb-2">Target Achievement</p>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(result.target_amount)}
                    </p>
                    <p className="text-white/50 text-sm mt-1">in {result.years} years</p>
                  </div>

                  <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-blue-300 text-sm">
                      💡 <strong>Tip:</strong> Start early and stay consistent. The power of compounding works best with time and discipline!
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <CalcIcon className="w-16 h-16 text-white/30 mb-4" />
                  <p className="text-white/50">
                    Enter your goals and click calculate to see your personalized SIP plan
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300 border border-white/20"
            >
              Need Expert Guidance? Contact Us
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InteractiveCrorepatiPage;
